import { HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';

import {
  ManageVacationsCriteriaDto,
  EmployeeObservationsDto,
  VacationsSeasonDto,
  SeasonCalendarDto
} from '@app/data/dtos/manage-vacations/manage-vacations.dto';
import {
  mapManageVacationsCriteria,
  mapObservations,
  mapSeasonCalendar,
  mapVacationsSeason
} from '@app/data/mappers/manage-vacations/manage-vacations.mapper';
import { LogisticsManageVacationsRepository } from '@app/domain/repositories/manage-vacations/logistics/logistics-manage-vacations.repository.contract';
import { ManageVacationsUserType } from '@app/entities/enums/manage-vacations/manage-vacations.enum';
import {
  LogisticsCalendarEvent,
  LogisticsProposal,
  LogisticsVacationsProposal
} from '@app/entities/models/manage-vacations/logistics-manage-vacations.model';
import { ManageVacationsCriteria } from '@app/entities/models/manage-vacations/manage-vacations-criteria.model';
import {
  EmployeeObservations,
  SeasonCalendar,
  VacationsSeason
} from '@app/entities/models/manage-vacations/manage-vacations.model';
import { TUserTypeCalendarProposal } from '@app/entities/types/manage-vacations.type';
import { ENV } from '@environments/environment';
import {
  SECTION_KEY,
  IGNORE_NO_CONTENT_ERROR
} from '@services/interceptors/error-sections.interceptor/error-sections.interceptor';

import { MRepository } from '@mercadona/core/utils/repository';

import {
  LogisticsCalendarDayDetailDto,
  LogisticsManageVacationsProposalsDto
} from '@/dtos/manage-vacations/logistics/logistics-manage-vacations.dto';
import {
  mapLogisticsCalendarEvents,
  mapLogisticsCreateProposalDto,
  mapLogisticsVacationsProposals
} from '@/mappers/manage-vacations/logistics/logistics-manage-vacations.mapper';

@Injectable({
  providedIn: 'root'
})
export class LogisticsManageVacationsRepositoryImpl extends MRepository implements LogisticsManageVacationsRepository {
  readonly officeBaseUrl = '/timemanage/v1/office/vacations-proposal';

  constructor() {
    super(`${ENV.API_URL}`);
  }

  getProposals(): Observable<LogisticsVacationsProposal> {
    return this.get<LogisticsManageVacationsProposalsDto>(this.officeBaseUrl, {
      context: new HttpContext().set(SECTION_KEY, 'manage-vacations').set(IGNORE_NO_CONTENT_ERROR, true)
    }).pipe(map(mapLogisticsVacationsProposals));
  }

  getVacationsCriteria(): Observable<ManageVacationsCriteria> {
    return this.get<ManageVacationsCriteriaDto>(`${this.officeBaseUrl}/criteria`, {
      context: new HttpContext().set(SECTION_KEY, 'manage-vacations')
    }).pipe(map(mapManageVacationsCriteria));
  }

  getVacationsSeason(): Observable<VacationsSeason> {
    return this.get<VacationsSeasonDto>(`${this.officeBaseUrl}/seasons`, {
      context: new HttpContext().set(SECTION_KEY, 'manage-vacations')
    }).pipe(map(mapVacationsSeason));
  }

  getCalendar(): Observable<SeasonCalendar<TUserTypeCalendarProposal>> {
    return this.get<SeasonCalendarDto>(`${this.officeBaseUrl}/calendar`, {
      context: new HttpContext().set(SECTION_KEY, 'manage-vacations')
    }).pipe(map(mapSeasonCalendar(ManageVacationsUserType.LOGISTICS)));
  }

  getDayDetail(day: string): Observable<LogisticsCalendarEvent[]> {
    let params = new HttpParams();
    params = params.set('day', day);

    return this.get<LogisticsCalendarDayDetailDto>(`${this.officeBaseUrl}/calendar/day-detail`, { params }).pipe(
      map(mapLogisticsCalendarEvents)
    );
  }

  createProposal(
    holidays: LogisticsProposal[],
    freeByHolidays: LogisticsProposal[],
    isDraft: boolean
  ): Observable<LogisticsVacationsProposal> {
    let params = new HttpParams();
    params = params.set('is_draft', isDraft);

    const createProposal = mapLogisticsCreateProposalDto(holidays, freeByHolidays);
    const request = {
      body: {
        ...createProposal
      },
      params
    };

    return this.put<LogisticsManageVacationsProposalsDto>(this.officeBaseUrl, request).pipe(
      map(mapLogisticsVacationsProposals)
    );
  }

  updateObservations(observations: string): Observable<EmployeeObservations> {
    const request = {
      body: {
        content: observations
      }
    };

    return this.put<EmployeeObservationsDto>(`${this.officeBaseUrl}/observations`, request).pipe(map(mapObservations));
  }
}
